import { ArrowsClockwise, Broadcast, Copy, DotsThree, Plus, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { Webhook } from "~/api/governanceTypes";
import {
  useCreateWebhook,
  useDeleteWebhook,
  useRotateWebhookSecret,
  useUpdateWebhook,
  useWebhooks,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { WebhookDeliveriesDialog } from "~/features/webhooks/WebhookDeliveriesDialog";
import {
  isWebhookFormValid,
  WebhookForm,
  type WebhookFormValue,
} from "~/features/webhooks/WebhookForm";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

/** A one-time secret reveal, in a copyable dialog — never a toast (§E-10). */
function SecretDialog({ webhook, onClose }: { webhook: Webhook; onClose: () => void }) {
  const { t } = useTranslation();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhook.secret);
      toast.success(t("Copied"));
    } catch {
      toast.error(t("Could not copy — select and copy the secret manually."));
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Signing secret")}
      description={t("This is the only time this secret is shown.")}
      closeLabel={t("Close")}
      size="sm"
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("Done")}
        </Button>
      }
    >
      <div className="flex items-center gap-2 rounded-sm border border-divider bg-bg-secondary px-3 py-2">
        <code className="min-w-0 flex-1 truncate text-small">{webhook.secret}</code>
        <IconButton icon={Copy} aria-label={t("Copy")} size="sm" onClick={copy} />
      </div>
    </Dialog>
  );
}

function CreateWebhookDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (w: Webhook) => void;
}) {
  const { t } = useTranslation();
  const createWebhook = useCreateWebhook();
  const [value, setValue] = useState<WebhookFormValue>({ url: "", events: [] });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("New webhook")}
      closeLabel={t("Close")}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!isWebhookFormValid(value) || createWebhook.isPending}
            onClick={() =>
              createWebhook.mutate(
                { url: value.url.trim(), events: value.events, enabled: true },
                { onSuccess: (webhook) => onCreated(webhook) },
              )
            }
          >
            {createWebhook.isPending ? t("Creating…") : t("Create")}
          </Button>
        </>
      }
    >
      <WebhookForm value={value} onChange={setValue} />
    </Dialog>
  );
}

function EditWebhookDialog({ webhook, onClose }: { webhook: Webhook; onClose: () => void }) {
  const { t } = useTranslation();
  const updateWebhook = useUpdateWebhook();
  const [value, setValue] = useState<WebhookFormValue>({
    url: webhook.url,
    events: webhook.events,
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Edit webhook")}
      closeLabel={t("Close")}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!isWebhookFormValid(value) || updateWebhook.isPending}
            onClick={() =>
              updateWebhook.mutate(
                {
                  id: webhook.id,
                  url: value.url.trim(),
                  events: value.events,
                  enabled: webhook.enabled,
                },
                {
                  onSuccess: () => {
                    onClose();
                    toast.success(t("Webhook updated"));
                  },
                },
              )
            }
          >
            {updateWebhook.isPending ? t("Saving…") : t("Save")}
          </Button>
        </>
      }
    >
      <WebhookForm value={value} onChange={setValue} />
    </Dialog>
  );
}

function WebhookRow({
  webhook,
  onEdit,
  onDeliveries,
  onRotated,
}: {
  webhook: Webhook;
  onEdit: () => void;
  onDeliveries: () => void;
  onRotated: (webhook: Webhook) => void;
}) {
  const { t } = useTranslation();
  const updateWebhook = useUpdateWebhook();
  const rotateSecret = useRotateWebhookSecret();
  const deleteWebhook = useDeleteWebhook();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const items: MenuItem[] = [
    { type: "button", title: t("Edit…"), onClick: onEdit },
    {
      type: "button",
      title: webhook.enabled ? t("Disable") : t("Enable"),
      onClick: () =>
        // The whole document must be echoed back — there is no partial update (§A.5 A52).
        updateWebhook.mutate({
          id: webhook.id,
          url: webhook.url,
          events: webhook.events,
          enabled: !webhook.enabled,
        }),
    },
    { type: "button", title: t("Deliveries…"), onClick: onDeliveries },
    {
      type: "button",
      title: t("Rotate secret"),
      icon: ArrowsClockwise,
      onClick: () =>
        rotateSecret.mutate(webhook.id, { onSuccess: (rotated) => onRotated(rotated) }),
    },
    { type: "separator" },
    {
      type: "button",
      title: t("Delete…"),
      icon: Trash,
      dangerous: true,
      onClick: () => setConfirmDelete(true),
    },
  ];

  return (
    <Row>
      <Broadcast size={18} aria-hidden className="shrink-0 text-text-tertiary" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-body text-text">{webhook.url}</p>
        <p className="truncate text-small text-text-tertiary">{webhook.events.join(", ")}</p>
      </div>
      <Pill tone={webhook.enabled ? "accent" : "neutral"}>
        {webhook.enabled ? t("Enabled") : t("Disabled")}
      </Pill>
      <Menu items={items} trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} />} />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("Delete webhook?")}
        description={webhook.url}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={deleteWebhook.isPending}
        onConfirm={() =>
          deleteWebhook.mutate(webhook.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              toast.success(t("Webhook deleted"));
            },
          })
        }
      />
    </Row>
  );
}

export function WebhooksSection() {
  const { t } = useTranslation();
  const { data: webhooks, isLoading } = useWebhooks();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Webhook | null>(null);
  const [viewingDeliveries, setViewingDeliveries] = useState<Webhook | null>(null);
  const [revealing, setRevealing] = useState<Webhook | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Text variant="small" className="text-text-tertiary">
          {t("Push events to your own endpoints.")}
        </Text>
        <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
          {t("New webhook")}
        </Button>
      </div>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !webhooks?.length ? (
        <Empty icon={Broadcast} message={t("No webhooks yet")} />
      ) : (
        <Rows>
          {webhooks.map((webhook) => (
            <WebhookRow
              key={webhook.id}
              webhook={webhook}
              onEdit={() => setEditing(webhook)}
              onDeliveries={() => setViewingDeliveries(webhook)}
              onRotated={(rotated) => setRevealing(rotated)}
            />
          ))}
        </Rows>
      )}

      {creating ? (
        <CreateWebhookDialog
          onClose={() => setCreating(false)}
          onCreated={(webhook) => {
            setCreating(false);
            setRevealing(webhook);
          }}
        />
      ) : null}
      {editing ? <EditWebhookDialog webhook={editing} onClose={() => setEditing(null)} /> : null}
      {viewingDeliveries ? (
        <WebhookDeliveriesDialog
          webhook={viewingDeliveries}
          onClose={() => setViewingDeliveries(null)}
        />
      ) : null}
      {revealing ? <SecretDialog webhook={revealing} onClose={() => setRevealing(null)} /> : null}
    </div>
  );
}
